/**
 * Sequence API contracts added in 1.7.4 (docs/dev/editor-1.7.4-build.md): Duplicate, and the "In to Out" range choice
 * of the EDL download, the export and Send to edit. The routes are stream A's (services/api production/sequences.ts,
 * exports.ts, handoffs.ts); the editor (stream B) calls them with these shapes.
 */
import { z } from 'zod';
import type { SequenceDocument } from './sequence.ts';

/** The longest sequence name (as POST /api/v1/sequences). */
export const SEQUENCE_NAME_MAX = 200;

/**
 * POST /api/v1/sequences/:id/duplicate (`sequence.edit`; the sequence and every source visible to the caller, else
 * 404 / 403 SOURCE_RESTRICTED as a save). Body: `name` (trimmed; left out or blank: duplicateName(the original's name)).
 * Copies the saved (working) document, the description and so the formats (they are in the document) into a new
 * sequence at revision 1; no versions, exports, hand-offs, lock or comments. Audited `sequence.duplicate` with
 * `{ from: <id>, name }`. 201 with SequenceDuplicateResponse.
 */
export const SequenceDuplicateRequestSchema = z.object({
  name: z.string().trim().max(SEQUENCE_NAME_MAX).optional(),
}).strict();
export type SequenceDuplicateRequest = z.infer<typeof SequenceDuplicateRequestSchema>;

/** The new sequence: the summary the sequence list shows (as POST /api/v1/sequences returns), its document and where it came from. */
export interface SequenceDuplicateResponse {
  id: string;
  name: string;
  description: string;
  revision: number;
  durationTicks: number;
  clips: number;
  frameRate: { num: number; den: number };
  width: number;
  height: number;
  updatedAt: string;
  updatedBy: string | null;
  createdBy: string | null;
  createdById: string | null;
  createdAt: string | null;
  lockedBy: null;
  document: SequenceDocument;
  /** The id of the sequence it was copied from. */
  duplicatedFrom: string;
}

/** The default name of a copy: "<name> copy", cut to fit SEQUENCE_NAME_MAX. */
export function duplicateName(name: string): string {
  const suffix = ' copy';
  return `${name.trim().slice(0, SEQUENCE_NAME_MAX - suffix.length).trimEnd()}${suffix}`;
}

/**
 * Which part of a sequence a download or render covers (1.7.4): 'all' (the default, as before) or 'marks', the
 * sequence In to Out (three-point.ts markRange; with no marks set, the whole sequence). Used by
 * GET /api/v1/sequences/:id/edl?range=marks, the export body's `range` and Send to edit's `range`; the server crops the
 * document with cropToRange (sequence-ops.ts) before building.
 */
export const SEQUENCE_RANGES = ['all', 'marks'] as const;
export type SequenceRange = (typeof SEQUENCE_RANGES)[number];
export const SequenceRangeSchema = z.enum(SEQUENCE_RANGES);
/** The EDL download's query (1.7.4). */
export const SequenceEdlQuerySchema = z.object({ range: SequenceRangeSchema.default('all') });
